/**
 * Knowledge bases, their documents, and Logs.
 *
 * One half of `AGENT_COPY`. The shape and the rules are documented on the
 * barrel in `../agent-copy.ts`; this file only holds entries.
 *
 * One vocabulary across both surfaces, so a word never changes meaning between
 * the list and the drawer: knowledge base 知识库, document 文档, chunk 分块,
 * embedding 向量, connector 连接器, tag 标签, run 运行, trace 追踪, span 跨度.
 */
export const KNOWLEDGE: Record<string, string> = {
	// -- the knowledge base list: columns, filters and sort
	Documents: "文档",
	// "Token 数" is the panel's billing wording, but it clips in the 0.6-width
	// column this header sits in; the counter noun is what the column already is.
	Tokens: "Token",
	Connectors: "连接器",
	Created: "创建时间",
	Owner: "所有者",
	"Last Updated": "最近更新",
	All: "全部",
	"With connectors": "有连接器",
	"Without connectors": "无连接器",
	"Has documents": "有文档",
	Empty: "空",
	Content: "内容",
	Clear: "清除",
	"Search knowledge bases...": "搜索知识库…",
	"Search members...": "搜索成员…",
	"Connectors: {value}": "连接器：{value}",
	"Connectors: {count} types": "连接器：{count} 种",
	"Content: {value}": "内容：{value}",
	"Content: {count} types": "内容：{count} 种",
	"Owner: {value}": "所有者：{value}",
	"Owner: {count} members": "所有者：{count} 人",
	"1 member": "1 名成员",

	// -- the list's rows, folders and bulk actions
	Folder: "文件夹",
	"Knowledge base": "知识库",
	"New folder": "新建文件夹",
	"New base": "新建知识库",
	Rename: "重命名",
	Delete: "删除",
	"Deleting...": "正在删除…",
	"Delete Folder": "删除文件夹",
	"Delete Selected": "删除所选",
	"this folder": "此文件夹",
	"Are you sure you want to delete ": "确定要删除 ",
	"? This also deletes the knowledge bases and folders inside it. You can restore them from Recently Deleted in Settings.":
		" 吗？其中的知识库和文件夹也会一并删除。可在设置的“最近删除”中恢复。",
	"? This also deletes the knowledge bases and folders inside the selected folders. You can restore them from Recently Deleted in Settings.":
		" 吗？所选文件夹中的知识库和文件夹也会一并删除。可在设置的“最近删除”中恢复。",
	"? You can restore them from Recently Deleted in Settings.": " 吗？可在设置的“最近删除”中恢复。",
	"Delete the search knowledge base first": "请先删除搜索知识库",
	"Select {n} or fewer items to move at once": "一次最多移动 {n} 个项目",
	"Select {n} or fewer items to delete at once": "一次最多删除 {n} 个项目",
	"Failed to rename folder": "重命名文件夹失败",
	"Failed to create folder": "创建文件夹失败",
	"Failed to delete folder": "删除文件夹失败",
	"Failed to move folder": "移动文件夹失败",
	"Failed to move knowledge base": "移动知识库失败",

	// -- one knowledge base: its documents table and header
	"Knowledge Base": "知识库",
	Document: "文档",
	Size: "大小",
	Chunks: "分块",
	Uploaded: "上传时间",
	Tags: "标签",
	Export: "导出",
	"New connector": "新建连接器",
	"New documents": "新增文档",
	"Search documents...": "搜索文档…",
	"Status: {value}": "状态：{value}",
	"Knowledge base not found": "未找到知识库",
	"This knowledge base may have been deleted or moved": "该知识库可能已被删除或移动",
	"Connected Sources": "已连接来源",

	// -- a document's indexing state, as the table's status column reads it
	Processing: "处理中",
	Enabled: "已启用",
	Disabled: "已停用",
	Unknown: "未知",
	Yes: "是",
	No: "否",
	"Failed to retry document processing": "重新处理文档失败",

	// -- deleting a base, a document, or a selection of them
	"Delete Knowledge Base": "删除知识库",
	"Delete Document": "删除文档",
	"Delete Documents": "删除文档",
	"this document": "此文档",
	"? ": " 吗？",
	"The knowledge base and all {count} document within it will be removed.": "知识库及其中的 {count} 个文档都将被删除。",
	"The knowledge base and all {count} documents within it will be removed.":
		"知识库及其中的 {count} 个文档都将被删除。",
	" You can restore it from Recently Deleted in Settings.": " 可在设置的“最近删除”中恢复。",
	"This document is synced from a connector. Deleting it will permanently exclude it from future syncs. To temporarily hide it from search, disable it instead.":
		"该文档由连接器同步而来，删除后将永久排除在后续同步之外。若只想暂时不参与检索，请改为停用。",
	"This will permanently delete the document.": "此操作将永久删除该文档。",
	" This action cannot be undone.": "此操作无法撤销。",
	"Are you sure you want to delete {count} document? ": "确定要删除 {count} 个文档吗？",
	"Are you sure you want to delete {count} documents? ": "确定要删除 {count} 个文档吗？",
	"This will permanently delete the selected document.": "此操作将永久删除所选文档。",
	"This will permanently delete the selected documents.": "此操作将永久删除所选文档。",
	"Delete {count} Document": "删除 {count} 个文档",
	"Delete {count} Documents": "删除 {count} 个文档",

	// -- the tag filter rows inside the documents filter popover
	"Filter by tags": "按标签筛选",
	"Clear all": "全部清除",
	"Add filter": "添加筛选",
	"Select tag": "选择标签",
	Operator: "运算符",
	"Remove tag filter": "移除标签筛选",
	and: "且",
	to: "至",
	From: "起始",
	To: "截止",
	"Select date": "选择日期",
	"true or false": "true 或 false",
	"Enter number": "输入数字",
	"Enter value": "输入值",

	// -- the list's context menus and its error boundary
	"Open in new tab": "在新标签页打开",
	"View tags": "查看标签",
	"Copy ID": "复制 ID",
	Pin: "置顶",
	Unpin: "取消置顶",
	Edit: "编辑",
	"All associated documents, chunks, and embeddings will be removed.": "相关的文档、分块和向量都将被删除。",
	"Are you sure you want to delete this knowledge base? ": "确定要删除此知识库吗？",
	"Failed to load knowledge": "加载知识库失败",
	"Something went wrong while loading your knowledge bases. Please try again.": "加载知识库时出错，请重试。",
	"Failed to load knowledge base": "加载知识库失败",
	"Something went wrong while loading this knowledge base. Please try again.": "加载该知识库时出错，请重试。",

	// -- the documents table's selection bar and row menu
	"{count} selected": "已选择 {count} 项",
	"Select all": "全选",
	Enable: "启用",
	Disable: "停用",
	Retry: "重试",
	"Open source": "打开来源",
	"Rename Document": "重命名文档",
	"Enter document name": "输入文档名称",
	"Renaming...": "正在重命名…",
	"Name is required": "名称不能为空",
	"Failed to rename document": "重命名文档失败",

	// -- creating and editing a knowledge base, and its chunking settings
	//
	// The footer button says "Create base" rather than "Create": the catalogue is
	// keyed by the English, and "Create" is already the marketing library's
	// authoring stage (创作). A knowledge base is 创建, and the two cannot share
	// one key — so the button says which thing it makes.
	"Create Knowledge Base": "创建知识库",
	"Create base": "创建知识库",
	"Creating...": "正在创建…",
	"Processing...": "正在处理…",
	"Uploading {done}/{total}...": "正在上传 {done}/{total}…",
	"Edit Knowledge Base": "编辑知识库",
	"Saving...": "正在保存…",
	Description: "描述",
	"Enter knowledge base name": "输入知识库名称",
	"Describe this knowledge base (optional)": "描述此知识库（可选）",
	"Chunking Configuration": "分块配置",
	"Chunking Strategy": "分块策略",
	"Max Size": "最大长度",
	"Min Size": "最小长度",
	Overlap: "重叠",
	tokens: "token",
	chars: "字符",
	"Min Chunk Size (characters)": "最小分块长度（字符）",
	"Max Chunk Size (tokens)": "最大分块长度（token）",
	"Overlap (tokens)": "重叠（token）",
	"1 token ≈ 4 characters. Max chunk size and overlap are in tokens.":
		"1 个 token 约等于 4 个字符。最大分块长度与重叠均以 token 计。",
	"Auto detects the best strategy based on file content type.": "自动模式会根据文件内容类型选择最合适的策略。",
	"Auto (detect from content)": "自动（根据内容判断）",
	"Text (word boundary splitting)": "文本（按词边界切分）",
	"Recursive (configurable separators)": "递归（可配置分隔符）",
	Sentence: "句子",
	"Token (fixed-size)": "Token（固定长度）",
	"Regex (custom pattern)": "正则（自定义模式）",
	"Regex Pattern": "正则表达式",
	"Text will be split at each match of this regex pattern.": "文本将在该正则的每个匹配处切分。",
	"Chunk Boundaries": "分块边界",
	"Preserve boundaries exactly. Recommended when each match is a discrete record (e.g. one QA pair per chunk).":
		"严格保留边界。当每个匹配都是一条独立记录时建议开启（例如每块一组问答）。",
	"Each match is its own chunk (don't merge)": "每个匹配单独成块（不合并）",
	"Custom Separators (optional)": "自定义分隔符（可选）",
	"Comma-separated list of delimiters in priority order, up to {max}. Leave empty for default separators.":
		"按优先级排列、以逗号分隔的分隔符列表，最多 {max} 个。留空则使用默认分隔符。",
	"Upload Documents": "上传文档",
	"PDF and UTF-8 text, CSV, Markdown, HTML, JSON, XML, YAML (max 20 files, 20MB each)":
		"PDF 及 UTF-8 文本、CSV、Markdown、HTML、JSON、XML、YAML（最多 20 个文件，每个 20MB）",
	"Selected Files": "已选文件",

	// -- what the create and edit forms refuse
	"Name must be less than 100 characters": "名称需少于 100 个字符",
	"Name cannot be empty": "名称不能为空",
	"Description must be {max} characters or less": "描述不能超过 {max} 个字符",
	"Min chunk size must be at least 1 character": "最小分块长度至少为 1 个字符",
	"Min chunk size must be less than 2000 characters": "最小分块长度需少于 2000 个字符",
	"Max chunk size must be at least 100 tokens": "最大分块长度至少为 100 个 token",
	"Max chunk size must be less than 4000 tokens": "最大分块长度需少于 4000 个 token",
	"Overlap must be non-negative": "重叠不能为负数",
	"Overlap must be less than 500 tokens": "重叠需少于 500 个 token",
	"Min chunk size (characters) must be less than max chunk size (tokens × 4)":
		"最小分块长度（字符）必须小于最大分块长度（token × 4）",
	"Overlap must be less than max chunk size": "重叠必须小于最大分块长度",
	"Regex pattern is required when using the regex strategy": "使用正则策略时必须填写正则表达式",
	"At most {max} separators are allowed": "最多只能设置 {max} 个分隔符",
	"Each separator must be {max} characters or less": "每个分隔符不能超过 {max} 个字符",
	"Please fix the highlighted fields": "请修正标红的字段",
	"An error occurred while processing files. Please try again.": "处理文件时出错，请重试。",
	"An unknown error occurred": "发生未知错误",
	"Failed to update knowledge base": "更新知识库失败",

	// -- adding documents to a base, and what the upload reports
	"New Documents": "新增文档",
	Upload: "上传",
	"Uploading...": "正在上传…",
	"Failed to upload {count} file(s)": "有 {count} 个文件上传失败",
	"No files provided for upload": "没有可上传的文件",
	"Knowledge base ID is required": "缺少知识库 ID",
	"workspaceId is required for upload": "上传缺少工作区 ID",
	"Unknown error occurred during upload": "上传过程中发生未知错误",

	// -- the base's tag definitions
	"Tags:": "标签：",
	"{count} defined": "已定义 {count} 个",
	"No tag definitions yet. Create your first tag to organize documents.": "还没有标签定义。创建第一个标签来组织文档。",
	"{count} document": "{count} 个文档",
	"{count} documents": "{count} 个文档",
	"Add Tag": "添加标签",
	"Create Tag": "创建标签",
	"Tag Name": "标签名称",
	"Enter tag name": "输入标签名称",
	"A tag with this name already exists": "已存在同名标签",
	"No available slots for this type. Choose a different type.": "该类型已无可用槽位，请选择其他类型。",
	"Select type": "选择类型",
	"{type} ({used}/{max})": "{type}（{used}/{max}）",
	Text: "文本",
	Number: "数字",
	Date: "日期",
	Boolean: "布尔值",
	"Delete Tag": "删除标签",
	selected: "所选",
	" tag? ": " 标签吗？",
	"Are you sure you want to delete the ": "确定要删除 ",
	"This will remove this tag from {count} document.": "此操作会从 {count} 个文档中移除该标签。",
	"This will remove this tag from {count} documents.": "此操作会从 {count} 个文档中移除该标签。",
	"Affected documents": "受影响的文档",
	'Documents using "{name}"': "使用“{name}”的文档",
	"{count} document is currently using this tag definition.": "当前有 {count} 个文档使用此标签定义。",
	"{count} documents are currently using this tag definition.": "当前有 {count} 个文档使用此标签定义。",
	"This tag definition is not being used by any documents. You can safely delete it to free up the tag slot.":
		"目前没有文档使用此标签定义，可以安全删除以释放标签槽位。",
	"and {count} more documents": "另有 {count} 个文档",

	// -- the base's connected sources: their state, their actions, their history
	//
	// A connector's `active` badge says "Connected" rather than "Active" — see the
	// note beside STATUS_CONFIG in connectors-section.tsx.
	Connected: "已连接",
	Syncing: "同步中",
	Error: "错误",
	"Per member": "按成员",
	"No connected sources yet. Connect an external source to automatically sync documents.":
		"还没有已连接的来源。连接外部来源即可自动同步文档。",
	"Last sync: {time}": "上次同步：{time}",
	"Next sync: {when}": "下次同步：{when}",
	pending: "待执行",
	"{count} docs": "{count} 个文档",
	"Updating access": "正在更新访问权限",
	"Sync history": "同步历史",
	"Hide history": "收起历史",
	"Sync now": "立即同步",
	"Sync queued": "同步已排队",
	"Syncing…": "同步中…",
	"Resume to sync": "恢复后才能同步",
	"Member sync is disabled": "成员同步已停用",
	"Full resync": "完整重新同步",
	Settings: "设置",
	Remove: "移除",
	"Removing…": "正在移除…",
	"Source actions": "来源操作",
	"Remove source": "移除来源",
	"This disconnects the source, stops future syncs, and deletes its member documents.":
		"这会断开该来源、停止后续同步，并删除其成员文档。",
	"This disconnects the source and stops future syncs. Synced documents remain unless you delete them below.":
		"这会断开该来源并停止后续同步。已同步的文档会保留，除非你在下方选择一并删除。",
	"Also delete synced documents": "同时删除已同步的文档",
	"Members cannot search this source until its access settings are fixed.": "在修复访问设置之前，成员无法检索此来源。",
	"Sync paused after repeated failures": "多次失败后已暂停同步",
	"Update the source account in Settings, then resume syncing.": "请在设置中更新来源账号，然后恢复同步。",
	"Reconnect the source account to resume syncing.": "重新连接来源账号即可恢复同步。",
	"Resume the source to retry syncing.": "恢复该来源即可重新同步。",
	Reconnect: "重新连接",
	"Additional permissions required": "需要额外的授权",
	"Update access": "更新访问权限",
	"Could not load sync history": "无法加载同步历史",
	"Loading sync history…": "正在加载同步历史…",
	"No sync history yet.": "暂无同步历史。",
	"Loading member sync history…": "正在加载成员同步历史…",
	"No member sync history yet.": "暂无成员同步历史。",
	"In progress…": "进行中…",
	Interrupted: "已中断",
	Partial: "部分完成",
	"No changes": "无变化",
	"{count} added": "新增 {count}",
	"{count} updated": "更新 {count}",
	"{count} deleted": "删除 {count}",
	"{count} failed": "失败 {count}",
	"{count} skipped": "跳过 {count}",
	"{count} connected": "已连接 {count} 人",
	"{count} need reconnecting": "{count} 人需要重新连接",
	"{count} not synced recently": "{count} 人近期未同步",
	"{count} member": "{count} 名成员",
	"{count} members": "{count} 名成员",

	// -- a connector's own documents list
	"Documents you can access": "你可访问的文档",
	"Document status": "文档状态",
	Included: "已纳入",
	"Included ({count})": "已纳入（{count}）",
	Excluded: "已排除",
	"Excluded ({count})": "已排除（{count}）",
	"Failed ({count})": "失败（{count}）",
	"Could not load documents": "无法加载文档",
	"Could not load more documents": "无法加载更多文档",
	"Load more documents": "加载更多文档",
	"No documents match your search": "没有匹配的文档",
	"No excluded documents": "没有已排除的文档",
	"No failed documents": "没有失败的文档",
	"No documents yet": "暂无文档",
	"Indexing failed": "索引失败",
	"Waiting to index": "等待索引",
	Indexing: "索引中",
	"Retry indexing": "重新索引",
	Exclude: "排除",
	"Open {name}": "打开 {name}",

	// -- a connector's configuration fields and their selectors
	"Search {noun}...": "搜索{noun}…",
	"Select {noun}": "选择{noun}",
	"Select {noun} first": "请先选择{noun}",
	"No {noun} found": "未找到{noun}",
	dependency: "上级字段",
	"Connect an account first": "请先连接账号",
	"No match — the list failed to load. Try reopening": "无匹配项 — 列表加载失败，请重新打开",
	"No match — too many to list. Try a more exact term": "无匹配项 — 结果过多，请使用更精确的关键词",
	"About {name}": "关于 {name}",
	"Switch {name} to {mode}": "将 {name} 切换为{mode}",
	"manual input": "手动输入",
	selector: "选择器",
	"Switch to manual input": "切换为手动输入",
	"Switch to selector": "切换为选择器",

	// -- how a source connects, and whose permissions it carries
	"Sync documents with": "同步文档所用账号",
	"Connected members": "已连接的成员",
	"Choose an account": "选择账号",
	"Member accounts": "成员账号",
	"Admin or service account": "管理员或服务账号",
	"Service account": "服务账号",
	"Slack app": "Slack 应用",
	"Connection method": "连接方式",
	"Sync using": "同步方式",
	"This connection method is not available in this {scope}.": "此{scope}不支持该连接方式。",
	"Everyone in this workspace can search these documents.": "本工作区的所有人都可以检索这些文档。",
	"Could not check Slack setup": "无法检查 Slack 配置",
	"Checking Slack setup…": "正在检查 Slack 配置…",
	"Set up Slack": "配置 Slack",

	// -- connecting a new source, and editing one that is already connected
	"Edit {name}": "编辑 {name}",
	"Connect Source": "连接来源",
	"Configure {service}": "配置 {service}",
	"Choose another source": "选择其他来源",
	"Search sources...": "搜索来源…",
	"No connectors available.": "没有可用的连接器。",
	'No sources found matching "{query}"': "没有与“{query}”匹配的来源",
	Authentication: "认证方式",
	"API key": "API 密钥",
	"API Key": "API 密钥",
	"Enter API key": "输入 API 密钥",
	"Connected account": "已连接的账号",
	"Indexing account": "索引账号",
	"Account for browsing": "用于浏览的账号",
	"Could not load accounts": "无法加载账号",
	"Connect {service} account": "连接 {service} 账号",
	"Connect another {service} account": "再连接一个 {service} 账号",
	"Select {service} account": "选择 {service} 账号",
	"Select your {service} account": "选择你的 {service} 账号",
	"Select a service account": "选择服务账号",
	"Select the account to sync as": "选择用于同步的账号",
	"Connect a {service} account in Integrations, then return here to select it.":
		"请先在「集成」中连接 {service} 账号，然后回到这里选择。",
	"More options": "更多选项",
	"Document details (optional)": "文档信息（可选）",
	"Metadata tags": "元数据标签",
	"Creating…": "正在创建…",
	"Create & Invite": "创建并邀请",
	"Connect & Sync": "连接并同步",

	// -- changing how a connected source authenticates
	"Re-enable per-member sync": "重新启用按成员同步",
	"Re-enabling…": "正在重新启用…",
	"Members and their documents are kept; the next sync restores their access.":
		"成员及其文档会保留，下次同步会恢复他们的访问权限。",
	"Switching…": "正在切换…",
	"Change indexing account": "更换索引账号",
	"Apply connection method": "应用连接方式",
	"Edit settings": "编辑设置",
	"The next sync uses this indexing account. Members keep their connected accounts and source permissions.":
		"下次同步将使用该索引账号。成员会保留各自已连接的账号与来源权限。",
	"The next sync uses this account and refreshes source permissions.": "下次同步将使用该账号，并刷新来源权限。",
	"Every workspace member can read every synced document once the next sync completes.":
		"下次同步完成后，工作区的每位成员都能读取全部已同步文档。",
	"Teammates are invited to connect their accounts. Documents become available after their next sync. Item limits are removed.":
		"系统会邀请团队成员连接各自的账号。文档在他们下次同步后可用，条目上限将取消。",
	"Documents become available after the next sync updates their source permissions. Item limits are removed.":
		"下次同步更新来源权限后文档即可用，条目上限将取消。",
	"Set {field} and save your settings before changing the connection method.":
		"请先设置 {field} 并保存，然后再更改连接方式。",

	// -- how often a source syncs
	"Sync Frequency": "同步频率",
	Live: "实时",
	"Every hour": "每小时",
	"Every 6 hours": "每 6 小时",
	Daily: "每天",
	Weekly: "每周",
	"Manual only": "仅手动",
	"Content and permissions update only when you sync. Documents become unavailable after 24 hours without a successful permission check.":
		"内容和权限只在你手动同步时更新。若 24 小时内没有成功的权限检查，文档将不可用。",
	"Permissions are checked on every sync.": "每次同步都会检查权限。",
	"Content follows this schedule. Member permissions are checked every hour.":
		"内容按此频率同步，成员权限每小时检查一次。",
	"Source permissions require a sync every hour, even when a longer interval is selected. Unchanged documents are not re-indexed.":
		"即使选择了更长的间隔，来源权限仍需每小时同步一次；未变更的文档不会重新索引。",

	// -- one document: its chunks table
	"Document not found": "未找到文档",
	"This document may have been deleted or moved": "该文档可能已被删除或移动",
	"Search chunks...": "搜索分块…",
	"New chunk": "新建分块",
	"New Chunk": "新建分块",
	Index: "序号",
	"Search failed": "搜索失败",
	"Couldn't load chunks": "无法加载分块",
	"Document processing pending...": "文档等待处理…",
	"Document processing in progress...": "文档处理中…",
	"Document processing failed": "文档处理失败",
	"Document not ready": "文档尚未就绪",
	"Loading chunk…": "正在加载分块…",
	"Chunk #{index}": "第 {index} 块",
	"Previous chunk": "上一个分块",
	"Next chunk": "下一个分块",
	"Copy content": "复制内容",
	"Delete Chunk": "删除分块",
	"Are you sure you want to delete this chunk? This action cannot be undone.": "确定要删除这个分块吗？此操作无法撤销。",
	"This will permanently delete the document and all {count} chunk within it.":
		"此操作将永久删除该文档及其中的 {count} 个分块。",
	"This will permanently delete the document and all {count} chunks within it.":
		"此操作将永久删除该文档及其中的 {count} 个分块。",

	// -- editing one chunk
	"Chunk content editor": "分块内容编辑器",
	"Enter the content for this chunk...": "输入该分块的内容…",
	"Enter chunk content...": "输入分块内容…",
	"This chunk is synced from a connector and cannot be edited": "该分块由连接器同步而来，无法编辑",
	"Read-only view": "只读视图",
	"Content cannot be empty": "内容不能为空",
	"Content exceeds maximum length (10,000 characters)": "内容超出长度上限（10,000 个字符）",
	Tokenizer: "分词器",
	"Token #{index}": "第 {index} 个 token",
	"Create Chunk": "创建分块",
	// "Created" belongs to the list column (创建时间); the chunk editor's save status
	// says which thing it created instead.
	"Chunk created": "已创建分块",
	Saved: "已保存",
	"Create failed": "创建失败",
	"Save failed": "保存失败",

	// -- a document's own tag values
	"Document Tags": "文档标签",
	True: "是",
	False: "否",
	"Enter or select tag name": "输入或选择标签名称",
	"Select value": "选择值",
	"Enter tag value": "输入标签值",
	"Save Changes": "保存更改",
	"Apply Tag": "应用标签",
	"Applying...": "正在应用…",
	"Maximum tag definitions reached. You can still use existing tag definitions, but cannot create new ones.":
		"标签定义数量已达上限。仍可使用现有标签定义，但无法新建。",

	// -- the run list: columns, header actions and its error boundary
	Dashboard: "概览",
	Cost: "费用",
	Duration: "时长",
	Trigger: "触发方式",
	"Search logs...": "搜索日志…",
	"Untitled Job": "未命名任务",
	"{count} credit": "{count} 积分",
	"{count} credits": "{count} 积分",
	"Failed to load logs": "加载日志失败",
	"Something went wrong while loading the logs. Please try again.": "加载日志时出错，请重试。",

	// -- what a run's status reads as
	Redacting: "脱敏中",
	"Cancelling...": "正在取消…",
	Info: "信息",

	// -- filtering the run list
	search: "搜索",
	"SUGGESTED FILTERS": "建议的筛选",
	"Workflow: {value}": "工作流：{value}",
	"Folder: {value}": "文件夹：{value}",
	"Trigger: {value}": "触发方式：{value}",
	"All statuses": "全部状态",
	"All workflows": "全部工作流",
	"All folders": "全部文件夹",
	"All triggers": "全部触发方式",
	"Search workflows...": "搜索工作流…",
	"Search folders...": "搜索文件夹…",
	"Search triggers...": "搜索触发方式…",
	Time: "时间",
	"Time Range": "时间范围",
	"1 selected": "已选 1 项",
	"{count} workflows": "已选 {count} 个工作流",
	"{count} folders": "已选 {count} 个文件夹",
	"{count} triggers": "已选 {count} 种触发方式",
	"Clear All Filters": "清除全部筛选",
	"All time": "全部时间",
	"Past 30 minutes": "最近 30 分钟",
	"Past hour": "最近 1 小时",
	"Past 6 hours": "最近 6 小时",
	"Past 12 hours": "最近 12 小时",
	"Past 24 hours": "最近 24 小时",
	"Past 3 days": "最近 3 天",
	"Past 7 days": "最近 7 天",
	"Past 14 days": "最近 14 天",
	"Past 30 days": "最近 30 天",
	"Custom range": "自定义范围",

	// -- acting on one run from the list
	"Run stopped": "运行已停止",
	"Failed to stop run": "停止运行失败",
	"Retry started": "已开始重试",
	"Failed to retry execution": "重试运行失败",
	"Stopping…": "正在停止…",
	"Cancel Run": "停止运行",
	"Open Workflow": "打开工作流",
	"Open Snapshot": "打开快照",
	"Filter by Workflow": "按此工作流筛选",
	"Clear Filters": "清除筛选",

	// -- the dashboard view
	"Error loading data": "数据加载失败",
	"No workflows": "没有工作流",
	"Create a workflow to see its execution history here": "创建工作流后，这里会显示它的运行历史",
	Runs: "运行次数",
	Errors: "错误数",
	Latency: "延迟",
	"Success Rate": "成功率",
	"No workflows found": "没有找到工作流",
	'No workflows found matching "{query}"': "没有与“{query}”匹配的工作流",
	"{succeeded}/{total} succeeded": "{succeeded}/{total} 成功",
	"Segment {index}": "第 {index} 段",

	// -- one run's detail drawer
	"Log Details": "日志详情",
	"Log details sidebar": "日志详情侧栏",
	"Resize log details panel": "调整日志详情面板宽度",
	"Previous log": "上一条日志",
	"Next log": "下一条日志",
	"Retry execution": "重试运行",
	Overview: "总览",
	Trace: "追踪",
	Job: "任务",
	"Run ID": "运行 ID",
	"Copy run ID": "复制运行 ID",
	Level: "级别",
	None: "无",
	Snapshot: "快照",
	"View Snapshot": "查看快照",
	Troubleshoot: "排查",
	"Troubleshoot in Chat": "在对话中排查",
	"Workflow Input": "工作流输入",
	"Workflow Output": "工作流输出",
	Total: "合计",
	"Base Run": "基础运行费",
	"<1 credit": "不足 1 积分",
	"0 credits": "0 积分",
	"{input} in · {output} out": "输入 {input} · 输出 {output}",
	"Actual Ondit credits charged for model and tool usage.": "模型和工具用量实际扣除的 Ondit 积分。",
	"No trace data available for this run": "本次运行没有追踪数据",
	"Loading trace…": "正在加载追踪…",
	"this run": "本次运行",
	'The "{name}" workflow run failed. Investigate the error in this run and help me fix it.':
		"“{name}”工作流运行失败。请排查本次运行中的错误并帮我修复。",
	"This workflow run failed. Investigate the error in this run and help me fix it.":
		"该工作流运行失败。请排查本次运行中的错误并帮我修复。",
	"Files ({count})": "文件（{count}）",

	// -- the frozen canvas a run was executed against
	"Workflow State": "工作流状态",
	"Loading run snapshot…": "正在加载运行快照…",
	"Loading the workflow state snapshot for this execution": "正在加载本次运行的工作流状态快照",
	"View the workflow state snapshot for this execution": "查看本次运行的工作流状态快照",
	"Could not load the workflow snapshot. Refresh and try again.": "无法加载工作流快照，请刷新后重试。",
	"Failed to load run snapshot: {message}": "加载运行快照失败：{message}",
	"Logged State Not Found": "未找到记录的状态",
	"This log was migrated from the old logging system. The workflow state at the time of this run is not available.":
		"这条日志由旧日志系统迁移而来，无法提供当时的工作流状态。",
	"Note: {note}": "备注：{note}",

	// -- the trace tab: one row per span, and the span's own detail pane
	"No trace data available": "没有追踪数据",
	"No matching spans": "没有匹配的跨度",
	"{count} span": "{count} 个跨度",
	"{count} spans": "{count} 个跨度",
	"Filter spans": "筛选跨度",
	"Jump to error": "跳到错误",
	"Copy raw trace": "复制原始追踪",
	"Expand all": "全部展开",
	"Collapse all": "全部收起",
	Collapse: "收起",
	Expand: "展开",
	"{pct}% of total": "占总时长 {pct}%",
	"{pct}% of parent": "占父级 {pct}%",
	"Select a span to see details.": "选择一个跨度以查看详情。",
	"custom block": "自定义节点",
	"Child run": "子运行",
	"Not available": "不可用",
	"Not expanded (nesting limit)": "未展开（超出嵌套层级）",
	"Not traced": "未记录追踪",
	Tries: "尝试次数",
	Provider: "服务商",
	"Finish reason": "结束原因",
	TTFT: "首字延迟",
	Throughput: "吞吐",
	"Input tokens": "输入 token",
	"Output tokens": "输出 token",
	"Total tokens": "总 token",
	"Cache read": "缓存读取",
	"Cache write": "缓存写入",
	"Reasoning tokens": "推理 token",
	"Error type": "错误类型",
	Iteration: "迭代",
	"Tool calls": "工具调用",
	"Error message": "错误信息",
	"Started {time}": "开始 {time}",
	"Ended {time}": "结束 {time}",
	// "Success" alone is the support form's 已提交 in the shell catalogue; a run that
	// finished says so in its own words.
	Succeeded: "成功",
};
